import type {
  RegionCreateRequest,
  RegionUpdateRequest,
  RegionResponse,
  RegionSetCountriesRequest,
  RegionCountriesResponse,
  BuiltInRegionGroupResponse,
} from '../types';
import type { RequestFn } from '../client';

/**
 * Custom regions (named country groups) scoped to a shop/profile, used by the
 * geo-aware payment-method availability overrides. Every endpoint is scoped by
 * `profileId`. Admin (`sk_*`) access required.
 */
export class Regions {
  constructor(private readonly request: RequestFn) {}

  /** Create a region. `POST /regions?profile_id=` */
  async create(profileId: string, params: RegionCreateRequest): Promise<RegionResponse> {
    return this.request('POST', '/regions', {
      body: params,
      query: { profile_id: profileId },
    });
  }

  /** List all regions for a profile. `GET /regions/list?profile_id=` */
  async list(profileId: string): Promise<RegionResponse[]> {
    return this.request('GET', '/regions/list', { query: { profile_id: profileId } });
  }

  /** List the built-in (global) region groups (EU/EEA/SEPA/LATAM/APAC). `GET /regions/groups` */
  async groups(): Promise<BuiltInRegionGroupResponse[]> {
    return this.request('GET', '/regions/groups');
  }

  /** Retrieve a region by id. `GET /regions/{regionId}?profile_id=` */
  async retrieve(regionId: string, profileId: string): Promise<RegionResponse> {
    return this.request('GET', `/regions/${encodeURIComponent(regionId)}`, {
      query: { profile_id: profileId },
    });
  }

  /** Update a region. `PUT /regions/{regionId}?profile_id=` */
  async update(
    regionId: string,
    profileId: string,
    params: RegionUpdateRequest,
  ): Promise<RegionResponse> {
    return this.request('PUT', `/regions/${encodeURIComponent(regionId)}`, {
      body: params,
      query: { profile_id: profileId },
    });
  }

  /** Delete a region. `DELETE /regions/{regionId}?profile_id=` */
  async delete(regionId: string, profileId: string): Promise<boolean> {
    return this.request('DELETE', `/regions/${encodeURIComponent(regionId)}`, {
      query: { profile_id: profileId },
    });
  }

  /** Get the countries that belong to a region. `GET /regions/{regionId}/countries?profile_id=` */
  async getCountries(regionId: string, profileId: string): Promise<RegionCountriesResponse> {
    return this.request('GET', `/regions/${encodeURIComponent(regionId)}/countries`, {
      query: { profile_id: profileId },
    });
  }

  /** Replace the full country membership of a region. `PUT /regions/{regionId}/countries?profile_id=` */
  async setCountries(
    regionId: string,
    profileId: string,
    params: RegionSetCountriesRequest,
  ): Promise<RegionCountriesResponse> {
    return this.request('PUT', `/regions/${encodeURIComponent(regionId)}/countries`, {
      body: params,
      query: { profile_id: profileId },
    });
  }
}
